import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import ReactDOM from 'react-dom';
import TrackingDetails from './TrackingDetails';
import OrderItems from './OrderItems';
import './styles.scss';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { OrderData, OrderTrackingResponse } from './types';
import { getInitialActiveTab } from './utils';
import { aloApiAuthFetch } from 'src/scripts/utils/alo-api-fetch';
import { InitialUrlSearchParams } from 'src/scripts/utils/environment';
import { LoadingMoreSpinner } from 'src/scripts/templates/product-listing-page/components/SmallUIComponents';

const BuilderReactComponent = lazy(
  () =>
    import(
      /* webpackChunkName: "BuilderReactComponent" */
      'src/scripts/templates/Builder/BuilderReactComponent'
    )
);

const urlParams = InitialUrlSearchParams;
const tokenFromURLparams = urlParams.get('token');
const emailFromURLparams = urlParams.get('email');
const orderReference = urlParams.get('order_reference') || '';

// Use token if available, otherwise fall back to email
const authParam = tokenFromURLparams
  ? `&token=${tokenFromURLparams}`
  : emailFromURLparams
  ? `&email=${encodeURIComponent(emailFromURLparams)}`
  : '';

const BrandingPage = () => {
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeShipmentTabIndex, setActiveShipmentTabIndex] = useState(0);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchOrderData = async () => {
      setIsLoading(true);
      setIsError(false);

      aloApiAuthFetch(`/v1/orders/tracking?order_reference=${encodeURIComponent(orderReference)}${authParam}`)
        .then((res) => res.json())
        .then((response: OrderTrackingResponse) => {
          const error = response.detail;
          if (error) {
            setIsError(true);
            console.log('Alo API error: ', error);
          } else {
            response ? setOrderData(response as OrderData) : setIsError(true);
            if (response) {
              setActiveShipmentTabIndex(getInitialActiveTab(response));
            }
          }
          setIsLoading(false);
        });
    };
    fetchOrderData();
  }, []);

  // Memoize BuilderContent components to prevent re-rendering on tab change
  const productCardBuilder = useMemo(
    () => (
      <ErrorBoundary>
        <Suspense fallback={<LoadingMoreSpinner />}>
          <BuilderReactComponent model="tracking-inline-block" />
        </Suspense>
      </ErrorBoundary>
    ),
    []
  );

  const bestSellersBuilder = useMemo(
    () => (
      <ErrorBoundary>
        <Suspense fallback={<LoadingMoreSpinner />}>
          <BuilderReactComponent model="tracking-collection-carousels" />
        </Suspense>
      </ErrorBoundary>
    ),
    []
  );

  if (isLoading) {
    return (
      <div className="branding-page-container">
        <div className="d-flex flex-column align-items-center justify-content-center">
          <LoadingMoreSpinner />
          <p>Loading Order Details</p>
        </div>
      </div>
    );
  }

  if (!orderData && !isError) {
    return null;
  }

  const { order_number: orderNumber, order_status_url: orderStatusUrl, shipments } = orderData || {};
  const activeShipment = shipments?.[activeShipmentTabIndex] || null;

  const handleShipmentTabChange = (tabIndex: number) => {
    setActiveShipmentTabIndex(tabIndex);
  };

  return (
    <div className="branding-page-container d-flex flex-column my-3xl mx-lg">
      <div className="tracking-main-section d-flex flex-row justify-content-center">
        <div className="top-section d-flex justify-content-between">
          <div className="branding-item">
            <TrackingDetails
              orderNumber={orderNumber}
              orderReference={orderReference}
              orderStatusUrl={orderStatusUrl}
              shipments={shipments}
              authParam={authParam}
              activeShipmentTabIndex={activeShipmentTabIndex}
              onTabChange={handleShipmentTabChange}
              isError={isError}
            />
          </div>
          <div className="branding-item mx-lg">
            <OrderItems
              orderReference={orderReference}
              orderStatusUrl={orderStatusUrl}
              lineItems={activeShipment?.line_items}
              authParam={authParam}
              isError={isError}
            />
          </div>
          <div className="branding-item builder-content">{productCardBuilder}</div>
        </div>
      </div>
      <div className="bottom-section">{bestSellersBuilder}</div>
    </div>
  );
};

const renderBrandingPage = () => {
  const mountNode = document.querySelector('[react-render-target="alo-branding-page"]');
  mountNode ? ReactDOM.render(<BrandingPage />, mountNode) : console.warn('missing render target: BrandingPage');
};

export const InitBrandingPage = () => {
  renderBrandingPage();
};
